import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  inject,
  OnInit,
  resource,
  signal,
  ViewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  InputCustomEvent,
  IonButton,
  IonButtons,
  IonCol,
  IonContent,
  IonGrid,
  IonHeader,
  IonIcon,
  IonInput,
  IonItem,
  IonLabel,
  IonRow,
  IonSelect,
  IonSelectOption,
  IonTitle,
  IonToolbar,
  ModalController,
} from '@ionic/angular/standalone';
import { SpinnerLoadingComponent } from '@okcargo-apps/layout-ionic';
import { IPlace } from '@okcargo-apps/shared-domain-ionic';
import { CountryStateService } from '@okcargo-apps/shared-infrastructure-ionic';
import { PlaceService } from '../place.service';

@Component({
  selector: 'lib-search-city-postalcode-modal',
  templateUrl: './search-city-postalcode-modal.component.html',
  styleUrls: ['./search-city-postalcode-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    IonLabel,
    IonContent,
    IonTitle,
    IonIcon,
    IonButton,
    IonButtons,
    IonToolbar,
    IonHeader,
    IonCol,
    IonRow,
    IonGrid,
    IonItem,
    IonInput,
    IonSelect,
    IonSelectOption,
    SpinnerLoadingComponent,
  ],
})
export class SearchCityPostalcodeModalComponent implements OnInit, AfterViewInit {
  private readonly placesService = inject(PlaceService);
  private readonly countryStateService = inject(CountryStateService);
  private readonly fb = inject(FormBuilder);
  private readonly modalCtrl = inject(ModalController);

  @ViewChild('postalCodeInput') postalCodeInput!: IonInput;

  public readonly formAddressEdit = this.fb.group({
    country: ['ES', Validators.required],
    postalCode: ['', Validators.minLength(2)],
  });

  public readonly searchTerm = signal<string>('');

  public readonly selectedCountry = toSignal(this.formAddressEdit.controls.country.valueChanges, {
    initialValue: this.formAddressEdit.controls.country.value ?? 'ES',
  });

  public readonly countries = this.countryStateService.countries;

  public readonly placesResource = resource({
    params: () => ({ term: this.searchTerm(), country: this.selectedCountry() }),
    loader: async ({ params: data, abortSignal }) => {
      if (!data.term || data.term.trim().length < 2 || !data.country) {
        return [];
      }
      return this.placesService.searchPlaces(data.term, data.country, abortSignal);
    },
  });

  ngOnInit(): void {
    const defaultCountry = this.countries().find((c) => c.isoCode === 'ES')?.isoCode || 'ES';
    if (this.formAddressEdit.controls.country.value !== defaultCountry) {
      this.formAddressEdit.patchValue({ country: defaultCountry }, { emitEvent: true });
    }
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.postalCodeInput?.setFocus();
    }, 100);
  }

  public handleChangeTerm(event: InputCustomEvent): void {
    this.searchTerm.set(event.detail.value ?? '');
  }

  public async handleSelectedPostalCode(place: IPlace): Promise<boolean> {
    const hasInvalidData = [place.city, place.postalCode, place.stateCode, place.countryCode].some(
      (value) => typeof value !== 'string' || value.trim().length === 0,
    );

    if (hasInvalidData) {
      console.error('Invalid place data received from the backend:', place);
      // TODO decide how to surface this error to the user
      return false;
    }

    try {
      const [countryName, stateObj] = await Promise.all([
        this.countryStateService.findCountryByCode(place.countryCode),
        this.countryStateService.findStateByCode(place.countryCode, place.stateCode),
      ]);

      const updatedPlace = {
        ...place,
        country: countryName,
        state: stateObj?.state,
      };

      return await this.modalCtrl.dismiss(updatedPlace, 'confirm');
    } catch (error) {
      console.error('Error processing the selected place:', error);
      return false;
    }
  }

  public closeModalWithCancel(): Promise<boolean> {
    return this.modalCtrl.dismiss(null, 'cancel');
  }
}
